import { useHistory, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import InputMask from 'react-input-mask';

import { useRegional, useUpdateRegional } from 'hooks/regionals';

import {
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Grid,
	HStack,
	Input,
	Select,
	Skeleton,
	Switch,
	Text,
	useColorModeValue,
	useToast
} from '@chakra-ui/react';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';

import { cnpjRegex } from 'variables/regex';
import { useGetClients } from 'hooks/clients';

const schema = yup.object().shape({
	name: yup.string().required('O campo Nome é obrigatório'),
	cnpj: yup.string().nullable().matches(cnpjRegex, 'Insira um CNPJ válido'),
	client_id: yup.string().required('O campo Cliente é obrigatório')
});

const EditRegional = () => {
	const { id } = useParams();
	const { data, loading } = useRegional(id);
	const regional = data?.getRegional || {};

	const {
		data: { listClients: clients = [] } = [],
		loading: loadingClients
	} = useGetClients();

	const history = useHistory();

	const methods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		resolver: yupResolver(schema)
	});
	const toast = useToast();

	const {
		register,
		reset,
		handleSubmit,
		formState: { errors }
	} = methods;

	const [updateRegional, { loading: loadingUpdate }] = useUpdateRegional({
		onSuccess: (data) => {
			reset();
			toast({
				title: 'Regional atualizada com sucesso.',
				status: 'success',
				duration: 5000,
				isClosable: true
			});
			history.push(
				`/sistema/administracao/regionais/${data.updateRegional.id}`
			);
		},
		onError: () => {
			toast({
				title: 'Ocorreu um erro',
				status: 'error',
				duration: 5000,
				isClosable: true
			});
		}
	});

	const onSubmit = (data) => {
		const variables = {
			input: { id, ...data }
		};
		updateRegional({ variables });
	};

	const inputHover = useColorModeValue('health.800', 'health.800');
	const textColor = useColorModeValue('gray.700', 'white');

	return (
		<Flex
			direction="column"
			minH="100vh"
			align="center"
			pt={{ sm: '120px', lg: '160px' }}
		>
			<Card w={{ md: '100%', lg: '50%' }}>
				<CardHeader mb="40px">
					<Flex direction="column">
						<Text
							color={textColor}
							fontSize="lg"
							fontWeight="bold"
							mb="3px"
						>
							Editar Regional
						</Text>
					</Flex>
				</CardHeader>
				<CardBody>
					<FormProvider {...methods}>
						<form
							style={{ width: '100%' }}
							onSubmit={handleSubmit(onSubmit)}
						>
							<Flex direction="column" w="100%">
								<Grid
									templateColumns={{
										sm: '1fr',
										md: 'repeat(2, 1fr)'
									}}
									gap="24px"
								>
									<FormControl>
										<HStack alignItems="center" mb="24px">
											<FormLabel
												color={textColor}
												fontWeight="bold"
												fontSize="xs"
												my="auto"
											>
												Ativo
											</FormLabel>
											{loading ? null : (
												<Switch
													{...register('is_active')}
													defaultChecked={
														regional.is_active
													}
													colorScheme="green"
												/>
											)}
										</HStack>
									</FormControl>
								</Grid>
								<Grid
									templateColumns={{
										sm: '1fr',
										md: 'repeat(2, 1fr)'
									}}
									gap="24px"
								>
									<FormControl isInvalid={errors.name}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Nome
										</FormLabel>
										{loading ? (
											<Skeleton height="40px" />
										) : (
											<Input
												{...register('name')}
												defaultValue={regional.name}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
										)}
										{errors.name && (
											<FormErrorMessage>
												{errors.name.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.phone}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Celular
										</FormLabel>
										{loading ? (
											<Skeleton height="40px" />
										) : (
											<Input
												as={InputMask}
												mask="(99) 99999-9999"
												defaultValue={regional.phone}
												{...register('phone')}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
										)}
										{errors.phone && (
											<FormErrorMessage>
												{errors.phone.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.cnpj}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											CNPJ
										</FormLabel>
										{loading ? (
											<Skeleton height="40px" />
										) : (
											<Input
												as={InputMask}
												mask="99.999.999/9999-99"
												defaultValue={regional.cnpj}
												{...register('cnpj')}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
										)}
										{errors.cnpj && (
											<FormErrorMessage>
												{errors.cnpj.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.client_id}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Cliente
										</FormLabel>
										{loading || loadingClients ? (
											<Skeleton height="40px" />
										) : (
											<Select
												placeholder="Selecione uma opção..."
												defaultValue={
													regional?.client_id
												}
												{...register('client_id')}
											>
												{clients &&
													clients.map((client) => (
														<option
															value={client.id}
														>
															{client.name}
														</option>
													))}
											</Select>
										)}
										{errors.client_id && (
											<FormErrorMessage>
												{errors.client_id.message}
											</FormErrorMessage>
										)}
									</FormControl>
								</Grid>
								<Flex
									w="full"
									justifyContent="flex-end"
									mt="24px"
								>
									<HStack spacing={4}>
										<Button
											disabled={loadingUpdate}
											variant="ghost"
											colorScheme="gray"
											alignSelf="flex-end"
											size="md"
											onClick={() => history.goBack()}
										>
											Cancelar
										</Button>
										<Button
											isLoading={loadingUpdate}
											disabled={loadingUpdate}
											colorScheme="green"
											type="submit"
											alignSelf="flex-end"
											size="md"
										>
											Atualizar
										</Button>
									</HStack>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</CardBody>
			</Card>
		</Flex>
	);
};

export default EditRegional;
